import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  CalculateIcon,
  CheckCircleOutilineIcon,
  DangerousIcon,
  EditIcon,
  InfoIcon,
  InputNumber,
  InputNumberVariantEnum,
  QuestionFillIcon,
  Select,
  showToast,
  SparksIcon,
  Text,
  Tooltip,
  TrashIcon,
  WarningIcon,
} from '@mercai/clever'

import { CatalogProductsProps } from './catalog-products.type'

import { ProductCard, SupplierForecastDrawerForm } from '@/components'
import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import {
  checkIfMarginIsInvalid,
  deepCopy,
  formatCurrency,
  generateUUID,
  useParamsState,
} from '@/helpers'
import {
  QUERIES_ADMIN,
  useCalculateUnitPrice,
  useCatalogCategoryList,
  useForecastsAndInventoriesList,
  useUpdateQuotationRequest,
} from '@/services'
import {
  CalculateUnitPricePayloadProps,
  CatalogProductProps,
  ForecastsAndInventoriesProps,
  QuotationAIComplementDataReasonFieldsItemProps,
  QuotationProductPackageEnum,
  QuotationProductProps,
  QuotationProps,
  QuotationRequestItemProps,
  QuotationRequestProps,
  SupplierProps,
} from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { ForecastsAndInventoriesTable } from '../forecasts-and-inventories-table'
import { ProductDrawerForm } from '../product-drawer-form'
import { SectionHeader } from '../section-header'
import { SelectCatalogProductDrawer } from '../select-catalog-product-drawer'
import {
  checkIfVerifiedSuppliersAreTheSame,
  generateButtonClasses,
  getQuotationSuggestedUnitPricePayload,
  getValueInMXN,
} from './catalog-products.constants'
import styles from './catalog-products.module.css'

export const CatalogProducts = ({
  position,
  validation,
  isEditable,
  quotationValues,
  setQuotationValues,
  catalogProducts,
  getMetrics,
  isRfq,
  quotationRequest,
  errors,
  dollarQuotation = 1,
  grossMargin = 2,
  cm3Margin = 7,
}: CatalogProductsProps) => {
  const { t: tEnums } = useTranslation('enums')

  const { t } = useTranslation('screenBusinessV2')

  const [selected, setSelected] = useState<number>(0)
  const [pressCalculate, setPressCalculate] = useState(false)

  const suppliersAretheSame = useMemo(() => {
    return checkIfVerifiedSuppliersAreTheSame(quotationValues?.products || [])
  }, [quotationValues?.products])

  const [, setForceRender] = useState(0)

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const queryClient = useQueryClient()

  const { data: catalogCategoryList } = useCatalogCategoryList()

  const { data: dataUnitPrice, mutateAsync: mutateAsyncUnitPrice } =
    useCalculateUnitPrice()

  const { mutateAsync: mutateAsyncUpdateQuotationRequest } =
    useUpdateQuotationRequest(quotationRequest?.uuid || '')

  const [productToUpdate, setProductToUpdate] = useState<
    Partial<QuotationRequestItemProps> | undefined
  >(quotationRequest?.items?.[selected])

  const [productToCreate, setProductToCreate] = useState<
    Partial<QuotationRequestItemProps> | undefined
  >()

  const [selectedForecastOrInventory, setSelectedForecastOrInventory] =
    useState<{
      [key: string]: string
    }>({})

  const [filter, setFilter] = useState({
    limit: 999,
    catalogProductUuid:
      quotationValues?.products?.[selected]?.catalogProduct?.uuid || '',
  })

  const { data, isLoading, refetch } = useForecastsAndInventoriesList({
    params: {
      ...filter,
    },
    isEnable: !!(isEditable && filter?.catalogProductUuid),
  })

  const onHandleValues = (
    key: string,
    value:
      | boolean
      | string
      | number
      | undefined
      | SupplierProps
      | CatalogProductProps,
  ) => {
    const quotationValuesAux = deepCopy(quotationValues) as QuotationProps

    quotationValuesAux.products[selected] = {
      ...quotationValuesAux?.products?.[selected],
      [key]: value,
      ...(key === 'supplier' && {
        supplierUuid: (value as SupplierProps)?.uuid,
      }),
      ...(key === 'catalogProduct' && {
        catalogProductUuid: (value as CatalogProductProps)?.uuid,
      }),
      ...(key === 'cost' && {
        cost: +Number(value || 0)?.toFixed?.(DECIMAL_PLACES_DEFAULT),
      }),
    }

    setQuotationValues({
      ...quotationValuesAux,
    })

    if (key === 'package') {
      getMetrics(quotationValuesAux)
    }
  }

  const getUnitPrice = async (quotation?: QuotationProps) => {
    let dataForSuggestedUnitPrice

    const data = deepCopy(quotation || quotationValues)

    if (!data) return

    const item = data?.products?.[selected] as QuotationProductProps

    data.products = [item]

    if (data) {
      dataForSuggestedUnitPrice = getQuotationSuggestedUnitPricePayload({
        quotationValues: data as QuotationProps,
      })
    }

    if (!dataForSuggestedUnitPrice) return

    if (
      dataForSuggestedUnitPrice?.totalFreightPrice > 0 &&
      !dataForSuggestedUnitPrice?.deliveryOption
    ) {
      return showToast({
        variant: 'warning',
        content: t('catalogProductsPricesUnitSalePriceDeliveryOptionWarning'),
      })
    }

    if (
      !dataForSuggestedUnitPrice?.items[0]?.quantity ||
      dataForSuggestedUnitPrice?.items[0].quantity <= 0
    ) {
      return showToast({
        variant: 'warning',
        content: t('catalogProductsPricesUnitSalePriceWarning'),
      })
    }

    const payload: CalculateUnitPricePayloadProps = {
      ...dataForSuggestedUnitPrice,
    }

    if (
      !payload.deliveryOption &&
      (!payload?.totalFreightPrice || payload?.totalFreightPrice === 0)
    ) {
      payload.deliveryOption = 'receive_at_address'
    }

    const response = await mutateAsyncUnitPrice(payload)

    if (!response.success) {
      let message = ''
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((response?.data as any)?.delivery_option) {
        message = t('catalogProductsPricesUnitSalePriceDeliveryOptionWarning')
      } else {
        message = t('catalogProductsPricesUnitSalePriceWarning')
      }
      return showToast({
        variant: 'warning',
        content: message,
      })
    }

    onHandleValues(
      'price',
      Number(response?.data?.items?.[0]?.unitProductPrice)?.toFixed?.(DECIMAL_PLACES_DEFAULT),
    )
  }

  const onOpenSupplierForecastDrawer = (formSupplierForecastUuid = '') => {
    setQueryParams({
      ...queryParams,
      form: 'create-supplier-product',
      formSupplierForecastUuid,
    })
  }

  const setQuotationValueByForecastOrInventory = (
    forecastOrInventory: ForecastsAndInventoriesProps,
  ) => {
    const quotationValuesAux = deepCopy(quotationValues) as QuotationProps

    quotationValuesAux.products[selected] = {
      ...quotationValuesAux?.products?.[selected],
      supplier: forecastOrInventory?.supplierProduct?.supplier as SupplierProps,
      supplierUuid:
        forecastOrInventory?.supplierProduct?.supplier?.uuid ||
        forecastOrInventory?.supplierProduct?.supplierUuid ||
        forecastOrInventory?.inventoryTransaction?.supplier?.uuid,
      supplierProductUuid: forecastOrInventory?.supplierProduct?.uuid,
      supplierProduct: undefined,
      supplierForecastUuid: forecastOrInventory?.forecastUuid || '',
      supplierForecast: undefined,
      supplyInventoryTransactionUuid:
        forecastOrInventory?.inventoryTransactionUuid || '',
      supplyInventoryTransaction: undefined,
      cost:
        (forecastOrInventory?.currency === 'USD'
          ? forecastOrInventory?.cost
          : +formatCurrency(
            Number(forecastOrInventory?.cost || 0) / Number(dollarQuotation),
            undefined,
            undefined,
            DECIMAL_PLACES_DEFAULT,
          )) || 0,
      package: forecastOrInventory?.supplierProduct?.package || '25kg_bags',
    }

    setQuotationValues({
      ...quotationValuesAux,
    })
  }

  const onCompleteCreate = async () => {
    const response = await refetch()

    if (response.data?.success) {
      const forecastOrInventory = {
        ...response?.data?.data?.results?.[0],
      }

      setQuotationValueByForecastOrInventory(forecastOrInventory)

      setSelectedForecastOrInventory((prev) => ({
        ...prev,
        [catalogProducts?.[selected]?.uuid || '']:
          forecastOrInventory?.forecast?.uuid ||
          forecastOrInventory?.forecastUuid ||
          forecastOrInventory?.inventoryTransaction?.uuid ||
          forecastOrInventory?.inventoryTransactionUuid ||
          '',
      }))

      setForceRender((prev) => prev + 1)
    }
  }

  const selectedForecastOrInventoryByCatalogProduct = useMemo(() => {
    const selectedForecastOrInventoryByCatalogProduct =
      data?.data?.results?.findIndex(
        (val) =>
          val?.forecastUuid ===
          selectedForecastOrInventory?.[
          catalogProducts?.[selected]?.uuid || ''
          ] ||
          val?.inventoryTransactionUuid ===
          selectedForecastOrInventory?.[
          catalogProducts?.[selected]?.uuid || ''
          ],
      )
    if (
      selectedForecastOrInventoryByCatalogProduct ||
      selectedForecastOrInventoryByCatalogProduct === 0
    ) {
      if (
        !quotationValues?.products?.[selected]?.supplierForecastUuid &&
        !quotationValues?.products?.[selected]?.supplyInventoryTransactionUuid
      ) {
        return undefined
      }

      return selectedForecastOrInventoryByCatalogProduct
    }

    return undefined
  }, [
    selectedForecastOrInventory,
    data?.data?.results,
    selected,
    catalogProducts,
  ])

  const onHandleSetSelectedSupplierProduct = (index: number) => {
    setForceRender((prev) => prev + 1)

    const forecastOrInventory = {
      ...data?.data?.results?.[index],
    }

    setQuotationValueByForecastOrInventory(forecastOrInventory)

    setSelectedForecastOrInventory((prev) => ({
      ...prev,
      [catalogProducts?.[selected]?.uuid || '']:
        forecastOrInventory?.forecast?.uuid ||
        forecastOrInventory?.forecastUuid ||
        forecastOrInventory?.inventoryTransaction?.uuid ||
        forecastOrInventory?.inventoryTransactionUuid ||
        '',
    }))
  }

  const onHandleRemoveCatalogProduct = () => {
    setSelectedForecastOrInventory((prev) => {
      const values = prev
      delete values[catalogProducts?.[selected]?.uuid || '']
      return {
        ...values,
      }
    })

    const quotationValuesAux = deepCopy(quotationValues) as QuotationProps

    quotationValuesAux.products.splice(selected, 1)

    setQuotationValues({
      ...quotationValuesAux,
    })
  }

  const onSelectProductItem = (productItem: CatalogProductProps) => {
    setSelectedForecastOrInventory((prev) => {
      const values = prev
      delete values[catalogProducts?.[selected]?.uuid || '']
      return {
        ...values,
      }
    })
    onHandleValues('catalogProduct', productItem)
  }

  const onAddProductItem = (productItem: CatalogProductProps) => {
    const quotationValuesAux = deepCopy(quotationValues) as QuotationProps

    quotationValuesAux.products.push({
      catalogProduct: productItem,
      catalogProductUuid: productItem?.uuid,
      uuid: generateUUID(),
    } as QuotationProductProps)

    setQuotationValues({
      ...quotationValuesAux,
    })
  }

  const onHandleRfqRemoveCatalogProduct = async () => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    quotationRequestAux.items.splice(selected, 1)

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
    })

    if (response.success) {
      setSelectedForecastOrInventory((prev) => {
        const values = prev
        delete values[catalogProducts?.[selected]?.uuid || '']
        return {
          ...values,
        }
      })

      setSelected(0)

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
      setQueryParams({ form: '' })
    }
  }

  const onHandleUpdateProduct = async () => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps
    quotationRequestAux.items[selected] = {
      ...quotationRequestAux?.items?.[selected],
      ...productToUpdate,
      uuid: quotationRequestAux?.items?.[selected]?.uuid,
      catalogProductUuid: productToUpdate?.catalogProduct?.uuid,
    }

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
    })

    if (response.success) {
      setSelectedForecastOrInventory((prev) => ({
        ...prev,
        [catalogProducts?.[selected]?.uuid || '']: '',
      }))

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
      setQueryParams({ form: '' })
    }
  }

  const onHandleAddProduct = async () => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    quotationRequestAux.items.push({
      ...productToCreate,
      catalogProductUuid: productToCreate?.catalogProduct?.uuid,
    } as QuotationRequestItemProps)

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
      setQueryParams({ form: '' })
    }
  }

  const ValueInMXN = ({
    value,
    decimalNumber = DECIMAL_PLACES_DEFAULT,
  }: {
    value: number
    decimalNumber?: number
  }) => {
    const valueInMXN = getValueInMXN(value, dollarQuotation, decimalNumber)

    return (
      <Text variant="text-small" className={styles['value-in-mxn']}>
        <InfoIcon /> {t('catalogProductsPricesValueInMXNLabel')} {valueInMXN}
      </Text>
    )
  }

  const getPriceWarning = ({
    suggestedUnitPrice = 0,
    minPrice = 0,
  }: {
    suggestedUnitPrice?: number
    minPrice?: number
  }) => {
    if (minPrice < suggestedUnitPrice) {
      return (
        <div className={styles['price-small-warning']}>
          <WarningIcon />
          <Text>{t('catalogProductsPricesTooSmallWarningLabel')}</Text>
        </div>
      )
    }

    return null
  }

  const getAIProps = (
    catalogProductUuid: string,
    key: string,
  ): {
    variant?: keyof typeof InputNumberVariantEnum
    iconRight?: ReactNode
  } => {
    const { reasonFields } = quotationValues?.aiComplementData || {}

    const item = reasonFields?.items?.find(
      (item) => item?.catalogProductUuid === catalogProductUuid,
    )

    const reasonField =
      item?.[key as keyof QuotationAIComplementDataReasonFieldsItemProps] || []

    const reasonFieldText =
      Array.isArray(reasonField) &&
      reasonField?.length > 0 &&
      reasonField
        ?.map((item: any) => item?.explanation) // eslint-disable-line
        .join('\n')

    if (reasonFieldText) {
      return {
        iconRight: (
          <Tooltip
            text={reasonFieldText}
            widthText={reasonFieldText?.length > 300 ? '30rem' : '15em'}
            orientation="top"
          >
            <div className={styles['ai-icon-box']}>
              <SparksIcon className={styles['ai-icon']} />
            </div>
          </Tooltip>
        ),
      }
    }

    return {}
  }

  const MarginIsInvalidMessage = useCallback(
    () =>
      checkIfMarginIsInvalid({ grossMargin, cm3Margin }) && (
        <Text className={styles['margin-information']}>
          {t('catalogProductsPricesIsInvalidMarginLabel')}
        </Text>
      ),
    [grossMargin, cm3Margin],
  )

  useEffect(() => {
    if (
      (quotationValues?.products?.[selected]?.supplierForecastUuid ||
        quotationValues?.products?.[selected]
          ?.supplyInventoryTransactionUuid) &&
      data?.data?.results
    ) {
      const forecastOrInventory = data?.data?.results?.find(
        (val) =>
          val?.forecastUuid ===
          quotationValues?.products?.[selected]?.supplierForecastUuid ||
          val?.inventoryTransactionUuid ===
          quotationValues?.products?.[selected]
            ?.supplyInventoryTransactionUuid,
      )

      setSelectedForecastOrInventory((prev) => ({
        ...prev,
        [catalogProducts?.[selected]?.uuid || '']:
          forecastOrInventory?.forecast?.uuid ||
          forecastOrInventory?.forecastUuid ||
          forecastOrInventory?.inventoryTransaction?.uuid ||
          forecastOrInventory?.inventoryTransactionUuid ||
          '',
      }))

      setForceRender((prev) => prev + 1)
    }
  }, [data?.data?.results, quotationValues?.products])

  useEffect(() => {
    setFilter({
      ...filter,
      catalogProductUuid:
        quotationValues?.products?.[selected]?.catalogProduct?.uuid || '',
    })

    setProductToUpdate(quotationRequest?.items?.[selected])
  }, [quotationValues, selected])

  useEffect(() => {
    if (pressCalculate) {
      getMetrics()
      setPressCalculate(false)
    }
  }, [quotationValues?.products?.[selected]?.price])

  return (
    <SectionHeader
      title={`${position}. ${isEditable
        ? t('sectionCatalogProductsEditTitle')
        : t('sectionCatalogProductsTitle')
        }`}
      key="catalog-products"
      isComplete={isEditable ? validation?.isComplete : undefined}
    >
      <div className={styles.core}>
        <div className={styles['row-buttons']}>
          {quotationValues?.products &&
            quotationValues?.products?.length > 0 && (
              <div className={styles['button-container']}>
                {quotationValues?.products?.map((product, i) => {
                  const isCompleted =
                    product?.isQuoted || validation?.productsIsQuoted?.[i]
                  return (
                    <Button
                      key={`${product?.uuid}-${i}`}
                      model="pill"
                      variant="primary-outline"
                      className={generateButtonClasses(
                        selected === i,
                        !!errors[`products[${i}]`],
                      )}
                      onClick={() => setSelected(i)}
                    >
                      {`${t('catalogProductsButton')} ${i + 1}`}
                      {isEditable && isCompleted && <CheckCircleOutilineIcon />}
                    </Button>
                  )
                })}
              </div>
            )}

          {isEditable &&
            (!quotationValues || quotationValues?.products?.length === 0) && (
              <Text>{t('catalogProductsSelectProductDescription')}</Text>
            )}

          {isEditable && (
            <Button
              variant="light"
              onClick={() => setQueryParams({ form: 'add-catalog-product' })}
            >
              {t('catalogProductsUnstructuredProductWarningButton')}
            </Button>
          )}
        </div>
        {quotationValues?.products && quotationValues?.products?.length > 0 && (
          <ProductCard
            img={
              quotationValues?.products?.[selected]?.catalogProduct?.attributes
                ?.mainImage as string
            }
            title={
              quotationValues?.products?.[selected]?.catalogProduct?.attributes
                ?.title ||
              catalogProducts?.[selected]?.product?.title ||
              '-'
            }
            category={
              quotationValues?.products?.[selected]?.catalogProduct?.category
                ?.name ||
              catalogProducts?.[selected]?.product?.category ||
              '-'
            }
            density={
              quotationValues?.products?.[selected]?.catalogProduct?.attributes
                ?.density ||
              catalogProducts?.[selected]?.product?.density ||
              '-'
            }
            fluidity={
              quotationValues?.products?.[selected]?.catalogProduct?.attributes
                ?.fluidity ||
              catalogProducts?.[selected]?.product?.fluidity ||
              '-'
            }
            isSimilarAccepted={
              catalogProducts?.[selected]?.isSimilarAccepted || false
            }
            quantity={quotationValues?.products?.[selected]?.quantity}
            purchaseFrequency={catalogProducts?.[selected]?.purchaseFrequency}
            targetPrice={catalogProducts?.[selected]?.targetPrice}
            observation={catalogProducts?.[selected]?.observation}
            actions={
              isEditable && (
                <div className={styles['button-actions']}>
                  {quotationValues?.products &&
                    quotationValues?.products?.length > 0 && (
                      <Button
                        model="square"
                        variant="secondary"
                        onClick={() =>
                          !isRfq
                            ? onHandleRemoveCatalogProduct()
                            : onHandleRfqRemoveCatalogProduct()
                        }
                      >
                        <TrashIcon />
                      </Button>
                    )}

                  <Button
                    model="square"
                    variant="secondary"
                    onClick={() =>
                      setQueryParams({
                        ...queryParams,
                        form: 'select-catalog-product',
                      })
                    }
                  >
                    <EditIcon />
                  </Button>
                </div>
              )
            }
          />
        )}

        {isRfq &&
          !quotationValues?.products?.[selected]?.catalogProduct &&
          catalogProducts?.[selected]?.product && (
            <Alert
              variant="danger"
              leftIcon={<DangerousIcon />}
              title={t('catalogProductsNotCatalogWarningMessageTitle')}
            >
              <div>
                {t('catalogProductsNotCatalogWarningMessageDescription')}
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  setQueryParams({
                    ...queryParams,
                    form: 'select-catalog-product',
                  })
                }
                className={styles['alert-not-catalog-button']}
              >
                {t('catalogProductsNotCatalogWarningMessageButton')}
              </Button>
            </Alert>
          )}

        {isEditable &&
          quotationValues?.products?.[selected]?.catalogProduct &&
          quotationValues?.products &&
          quotationValues?.products?.length > 0 && (
            <div className={styles['supplier-products-table']}>
              <ForecastsAndInventoriesTable
                data={data?.data?.results}
                onClick={() => setForceRender((prev) => prev + 1)}
                isLoading={isLoading}
                onSelectedItem={onHandleSetSelectedSupplierProduct}
                selectedItem={selectedForecastOrInventoryByCatalogProduct}
                onOpenSupplierForecastDrawer={onOpenSupplierForecastDrawer}
              />
              {!suppliersAretheSame && (
                <Text variant="text-base" className={styles.warning}>
                  {t('catalogProductsSupplierAreTheSame')}
                </Text>
              )}
            </div>
          )}

        {isEditable &&
          selectedForecastOrInventory?.[
          catalogProducts?.[selected]?.uuid || ''
          ] !== undefined && (
            <div className={styles['supplier-product-prices']}>
              <Text
                variant="text-medium"
                className={styles['supplier-product-prices-title']}
              >
                <strong>{t('catalogProductsPricesTitle')}</strong>
                <div>
                  <strong>
                    {formatCurrency(1, 'es-MX', 'USD', 0)}
                    {' = '}
                    {formatCurrency(dollarQuotation, 'es-MX', 'MXN', 4)}
                  </strong>
                </div>
              </Text>
              <div className={styles['supplier-product-prices-content']}>
                <div>
                  <Text variant="text-base" className={styles.label}>
                    {t('catalogProductsPricesUnitCostMercaiLabel')}{' '}
                    <Tooltip
                      text={t('catalogProductsPricesUnitCostMercaiTooltip')}
                      widthText="16rem"
                      orientation="top"
                    >
                      <QuestionFillIcon />
                    </Tooltip>
                    <span>*</span>
                  </Text>
                  <InputNumber
                    showIcons
                    value={quotationValues?.products?.[selected]?.cost}
                    onChange={(newValue) => onHandleValues('cost', newValue)}
                    placeholder={t(
                      'catalogProductsPricesUnitCostMercaiPlaceholder',
                    )}
                    decimalPrecision={DECIMAL_PLACES_DEFAULT}
                    prefix="$ "
                    onBlur={() => getMetrics()}
                    error={
                      errors[`products[${selected}]`]?.cost as unknown as string
                    }
                    {...getAIProps(
                      quotationValues?.products?.[selected]
                        ?.catalogProductUuid || '',
                      'cost',
                    )}
                  />
                  <ValueInMXN
                    value={quotationValues?.products?.[selected]?.cost || 0}
                  />
                </div>
                <div>
                  <Text variant="text-base" className={styles.label}>
                    {t('catalogProductsPricesTraderCommissionLabel')}
                    <Tooltip
                      text={t('catalogProductsPricesTraderCommissionTooltip')}
                      widthText="16rem"
                      orientation="top"
                    >
                      <QuestionFillIcon />
                    </Tooltip>
                  </Text>
                  <InputNumber
                    showIcons
                    value={
                      quotationValues?.products?.[selected]?.traderCommission
                    }
                    onChange={(newValue) =>
                      onHandleValues('traderCommission', newValue)
                    }
                    placeholder={t(
                      'catalogProductsPricesTraderCommissionPlaceholder',
                    )}
                    decimalPrecision={DECIMAL_PLACES_DEFAULT}
                    prefix="$ "
                    onBlur={() => getMetrics()}
                    error={
                      errors[`products[${selected}]`]
                        ?.traderCommission as unknown as string
                    }
                  />
                  <ValueInMXN
                    value={
                      quotationValues?.products?.[selected]?.traderCommission ||
                      0
                    }
                    decimalNumber={5}
                  />
                </div>

                <div>
                  <Text variant="text-base" className={styles.label}>
                    {t('catalogProductsPricesQuantityLabel')} <span>*</span>
                  </Text>
                  <InputNumber
                    showIcons
                    value={quotationValues?.products?.[selected]?.quantity}
                    onChange={(newValue) =>
                      onHandleValues('quantity', newValue)
                    }
                    placeholder={t('catalogProductsPricesQuantityPlaceholder')}
                    decimalPrecision={0}
                    suffix=" kg"
                    onBlur={() => getMetrics()}
                    error={
                      errors[`products[${selected}]`]
                        ?.quantity as unknown as string
                    }
                  />
                </div>

                <div>
                  <Text variant="text-base" className={styles.label}>
                    {t('catalogProductsPricesPackageLabel')} <span>*</span>
                  </Text>
                  <Select
                    placeholder={t('catalogProductsPricesPackagePlaceholder')}
                    options={Object.entries(QuotationProductPackageEnum).map(
                      ([key]) => ({
                        label: tEnums(`quotation_items_package_${key}`),
                        value: key,
                      }),
                    )}
                    value={quotationValues?.products?.[selected]?.package}
                    onChange={(newValue) => {
                      onHandleValues('package', newValue)
                    }}
                    error={
                      errors[`products[${selected}]`]
                        ?.package as unknown as string
                    }
                  />
                </div>
              </div>
              <div className={styles['supplier-product-prices-suggested']}>
                <div
                  className={styles['supplier-product-prices-suggested-title']}
                >
                  <Text variant="text-medium">
                    <strong>
                      {t('catalogProductsPricesUnitSalePriceTitle')}
                    </strong>
                  </Text>
                  <div className={styles['buttons-calculate']}>
                    <Button
                      variant="ghost"
                      className={styles['calculate-price-button']}
                      onClick={() => {
                        setPressCalculate(true)
                        getUnitPrice()
                      }}
                    >
                      {t('catalogProductsPricesUnitSalePriceButton')}
                      <CalculateIcon />
                    </Button>
                  </div>
                </div>

                <MarginIsInvalidMessage />

                <div
                  className={
                    styles['supplier-product-prices-content-suggested']
                  }
                >
                  <div>
                    <Text
                      variant="text-base"
                      className={`${styles.label} ${styles.suggested}`}
                    >
                      {t('catalogProductsClientPriceTargetLabel')}
                    </Text>
                    <InputNumber
                      disabled
                      value={catalogProducts?.[selected]?.targetPrice || 0}
                      placeholder={t(
                        'catalogProductsClientPriceTargetPlaceholder',
                      )}
                      decimalPrecision={DECIMAL_PLACES_DEFAULT}
                      prefix="$ "
                      onBlur={() => getMetrics()}
                    />
                  </div>
                  <div>
                    <Text
                      variant="text-base"
                      className={`${styles.label} ${styles.suggested}`}
                    >
                      {t('catalogProductsPricesSuggestedPriceLabel')}
                    </Text>
                    <InputNumber
                      disabled
                      value={
                        dataUnitPrice?.data?.items?.[selected]
                          ?.unitProductPrice || 0
                      }
                      placeholder={t(
                        'catalogProductsPricesUnitSuggestedSalePricePlaceholder',
                      )}
                      decimalPrecision={DECIMAL_PLACES_DEFAULT}
                      prefix="$ "
                      onBlur={() => getMetrics()}
                    />

                    <Text variant="text-base" className={styles.label}>
                      <InfoIcon />
                      {t(
                        'catalogProductsPricesSuggestedPriceWithoutIvaWarning',
                      )}
                    </Text>
                  </div>
                  <div>
                    <div className={styles['custom-label']}>
                      <Text variant="text-base" className={styles.label}>
                        {t('catalogProductsPricesUnitSalePriceLabel')}
                        {' - '}
                      </Text>
                      <Text
                        variant="text-base"
                        className={`${styles.label} ${styles.greened}`}
                      >
                        {t('catalogProductsPricesUnitSalePriceWithouIvaLabel')}
                        <span>*</span>
                      </Text>
                    </div>
                    <InputNumber
                      showIcons
                      value={quotationValues?.products?.[selected]?.price}
                      onChange={(newValue) =>
                        onHandleValues('price', Number(newValue)?.toFixed?.(DECIMAL_PLACES_DEFAULT))
                      }
                      placeholder={t(
                        'catalogProductsPricesUnitSalePricePlaceholder',
                      )}
                      decimalPrecision={DECIMAL_PLACES_DEFAULT}
                      prefix="$ "
                      onBlur={() => getMetrics()}
                      error={
                        errors[`products[${selected}]`]
                          ?.price as unknown as string
                      }
                      {...getAIProps(
                        quotationValues?.products?.[selected]
                          ?.catalogProductUuid || '',
                        'price',
                      )}
                    />
                    {getPriceWarning({
                      suggestedUnitPrice:
                        dataUnitPrice?.data?.items?.[selected]
                          ?.unitProductPrice,
                      minPrice: quotationValues?.products?.[selected]?.price,
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

        {!isRfq && (
          <>
            <SelectCatalogProductDrawer
              isOpen={queryParams?.form === 'select-catalog-product'}
              onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
              productItem={
                quotationValues?.products?.[selected]?.catalogProduct
              }
              onSelectProductItem={onSelectProductItem}
            />
            <SelectCatalogProductDrawer
              isOpen={queryParams?.form === 'add-catalog-product'}
              onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
              onSelectProductItem={onAddProductItem}
            />
          </>
        )}

        {isRfq && (
          <>
            <ProductDrawerForm
              isOpen={queryParams?.form === 'select-catalog-product'}
              onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
              catalogCategoryList={catalogCategoryList?.data?.results || []}
              values={productToUpdate as Partial<QuotationRequestItemProps>}
              onChangeValues={(values) => setProductToUpdate(values)}
              onHandleSubmit={onHandleUpdateProduct}
            />

            <ProductDrawerForm
              isOpen={queryParams?.form === 'add-catalog-product'}
              onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
              catalogCategoryList={catalogCategoryList?.data?.results || []}
              values={productToCreate as Partial<QuotationRequestItemProps>}
              onChangeValues={(values) => setProductToCreate(values)}
              onHandleSubmit={onHandleAddProduct}
            />
          </>
        )}

        {quotationValues?.products?.[selected]?.catalogProduct?.uuid && (
          <SupplierForecastDrawerForm
            isOpen={queryParams?.form === 'create-supplier-product'}
            onHandleOpen={() =>
              setQueryParams({
                ...queryParams,
                form: '',
                formSupplierForecastUuid: '',
              })
            }
            catalogProductUuid={
              quotationValues?.products?.[selected]?.catalogProduct?.uuid ||
              quotationValues?.products?.[selected]?.catalogProductUuid ||
              ''
            }
            supplierForecastUuid={queryParams?.formSupplierForecastUuid}
            onCompleteCreate={onCompleteCreate}
          />
        )}
      </div>
    </SectionHeader>
  )
}
